import { useState, useEffect, useRef } from 'react';

// Data now typically arrives in well under a second, which made the bar jump
// from ~5% straight to ~95% with no visible motion. To keep the "loading" feel,
// we animate a simulated crawl toward SIMULATED_CAP (decelerating, never quite
// reaching it) for as long as the real fetch takes, then do a quick final fill
// to 100 the moment data actually arrives — so it never claims to be done early.
const SIMULATED_CAP = 90;
const SIMULATED_RATE = 0.06;
const FINAL_FILL_DURATION = 300;

const usePageLoadProgress = (fetchDataFunction) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isDataReady, setIsDataReady] = useState(false);
  const [apiDataResults, setApiDataResults] = useState(null);

  const animationFrameRef = useRef(null);
  const progressRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    setLoadingProgress(0);
    setIsDataReady(false);
    setApiDataResults(null);
    progressRef.current = 0;

    const simulateStep = () => {
      if (cancelled) return;
      progressRef.current +=
        (SIMULATED_CAP - progressRef.current) * SIMULATED_RATE;
      setLoadingProgress(progressRef.current);
      animationFrameRef.current = requestAnimationFrame(simulateStep);
    };
    animationFrameRef.current = requestAnimationFrame(simulateStep);

    const fetchData = async () => {
      const fetchedData = await fetchDataFunction();
      if (cancelled) return;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      const startProgress = progressRef.current;
      let startTime = null;

      const fillToComplete = (currentTime) => {
        if (cancelled) return;
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const fraction = Math.min(elapsed / FINAL_FILL_DURATION, 1);
        const current = startProgress + (100 - startProgress) * fraction;
        progressRef.current = current;
        setLoadingProgress(current);

        if (fraction < 1) {
          animationFrameRef.current = requestAnimationFrame(fillToComplete);
        } else {
          setApiDataResults([fetchedData]);
          setIsDataReady(true);
          animationFrameRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(fillToComplete);
    };

    fetchData();

    return () => {
      cancelled = true;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return { loadingProgress, isDataReady, apiDataResults };
};

export default usePageLoadProgress;
